import { roadMapBridge } from '../render/roadMapBridge';
import { useWorldStore } from '../state/worldStore';
import { IS_IOS } from '../config';
import { atmosLevel } from '../services/atmosphere';

// HEARING A SOURCE IN THE STREET — shared by the plaza stage (components/city/
// CityStage) and the street's other music (components/city/CitySounds), so the
// two carry the same way and a wall muffles them the same.

/** Is there a building on the line from the listener to the source? (road map class 3), the last `skipEnd` units — the source's own frontage — excepted. */
export function walled(ax, az, bx, bz, skipEnd = 3) {
  const m = roadMapBridge.map;
  if (!m?.cls) return false;
  const u = m.unitsPerTexel || m.rect.w / m.w;
  const L = Math.hypot(bx - ax, bz - az);
  for (let s = 0.5; s < L - skipEnd; s += 0.4) {
    const i = Math.floor((ax + ((bx - ax) * s) / L - m.rect.minX) / u);
    const r = Math.floor((az + ((bz - az) * s) / L - m.rect.minZ) / u);
    if (i < 0 || r < 0 || i >= m.w || r >= m.h) continue;
    if (m.cls[r * m.w + i] === 3) return true;
  }
  return false;
}

/** Loud up to `full` units, gone at `far`, falling off like a PA in between. */
export function falloff(d, full, far, peak = 0.85) {
  if (d <= full) return peak;
  if (d >= far) return 0;
  const k = (d - full) / (far - full);
  return peak * Math.pow(1 - k, 1.6);
}

/* ── VOICES FIRST, WHERE VOLUME DOES NOT WORK (2026-11-05) ─────────────────
   Khari: "My main concern is the conversations, which are not currently heard.
   Perhaps the music is winning over them. If a conversation is entered on
   safari and multiple audio players are competing, the conversation should
   win." And of the music's own fades and dips: "all of these are fine as muted
   if safari cannot handle them."
   iOS ignores `volume` on media elements (reading it back always gives 1), so
   every "step back under the voices" here was a no-op on an iPhone: the street's
   music, the stage's song and the station played at full over a conversation.
   Where volume does not work, while anyone is speaking to you — a conversation
   heard or stepped into (CityTalk), a chat open with someone (`chatAvatar`), a
   duck or dip of the station (audioEngine: every narration, guide, clerk, call
   and story line already asks for one) — MUSIC IS MUTED: the station, the
   street's music, the stage's songs, a venue's or room's soundtrack, a story's
   score (`setLevel(el, v, { music: true })`). Voices never are. Desktop and
   Android (volume works) keep their dips exactly as before. */
/* ⚠ iOS BY NAME FIRST (2026-11-05: on Khari's iPhone the radio kept playing
   over a stepped-into conversation — the rule never switched on). Reading
   `volume` back is documented to give 1 on iOS, but a getter can report what it
   was given while the speaker ignores it; iOS never lets a page set volume, so
   config IS_IOS (iPadOS-as-Mac included) decides, and the read-back only adds
   anything else that cannot. */
export const volumeWorks = !IS_IOS && (() => {
  try { const a = document.createElement('audio'); a.volume = 0.5; return Math.abs(a.volume - 0.5) < 0.01; } catch { return true; }
})();
const holds = new Map();      // owner → true, while they are speaking
const letGo = new Map();      // owner → timer, the beat before a hold ends
const hushWatchers = new Set();
const notify = () => hushWatchers.forEach((fn) => { try { fn(musicHushed()); } catch { /* a watcher's own trouble */ } });
/** Music steps out for voices right now (only where volume does not work). */
export function musicHushed() { return !volumeWorks && holds.size > 0; }
/** Who is holding the music out (for ?audiodebug). */
export function voiceHolders() { return [...holds.keys()]; }
/** Told when musicHushed() changes (audioEngine mutes the station with it). */
export function onHushChange(fn) { hushWatchers.add(fn); return () => hushWatchers.delete(fn); }
/**
 * `owner` is speaking (or stopped). `releaseMs`: wait this long before letting
 * go, so the gaps between lines do not bring the music back for a breath.
 */
export function voiceHold(owner, on, { releaseMs = 0 } = {}) {
  const was = musicHushed();
  if (on) {
    clearTimeout(letGo.get(owner)); letGo.delete(owner);
    holds.set(owner, true);
  } else if (holds.has(owner) && !letGo.has(owner)) {
    if (releaseMs > 0) {
      letGo.set(owner, setTimeout(() => { letGo.delete(owner); const w = musicHushed(); holds.delete(owner); if (w !== musicHushed()) notify(); }, releaseMs));
      return;
    }
    holds.delete(owner);
  }
  if (was !== musicHushed()) notify();
}
// A conversation entered with someone (the street, a room's desk, a café table): voices first until it closes.
try {
  voiceHold('chat', !!useWorldStore.getState().chatAvatar);
  useWorldStore.subscribe((st, prev) => { if (st.chatAvatar !== prev.chatAvatar) voiceHold('chat', !!st.chatAvatar); });
} catch { /* no store (a test): nothing to follow */ }

// iOS ignores `volume` on media elements: out of earshot is MUTED (CityTalk).
// `music`: muted too while voices come first (above).
export function setLevel(el, v, { music = false } = {}) {
  const x = Math.max(0, Math.min(1, v));
  // Heard through its place (services/atmosphere): the level is its gain, and the element stays at full.
  if (atmosLevel(el, x)) return;
  el.volume = x;
  const mute = x < 0.03 || (music && musicHushed());
  if (el.muted !== mute) el.muted = mute;
}
